import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_WorkRequest_FindAllForOrderReportFilter_VM_WorkRequest } from 'ab-api/web/workRequest/findAllForOrderReportFilter';

import Dropdown from 'af-components/Controls/Dropdown';

import * as WorkRequestActions from 'af-actions/workRequests';

import styles from './styles.module.scss';

export class JobFilterOption {
	id: number;
	jobCode: string;
	title: string;
	customerCompany: Nullable<string>;
	isInternal: boolean;

	constructor(job: W_WorkRequest_FindAllForOrderReportFilter_VM_WorkRequest) {
		this.id = job.id;
		this.title = job.title;
		this.jobCode = job.jobCode;
		this.customerCompany = job.customerCompany;
		this.isInternal = job.isInternal;
	}

	static bulkConstructor = (_jobs: W_WorkRequest_FindAllForOrderReportFilter_VM_WorkRequest[]) => _jobs.map((_job) => JobFilterOption._constructorMap(_job));

	private static _constructorMap = (_job: W_WorkRequest_FindAllForOrderReportFilter_VM_WorkRequest) => new JobFilterOption(_job);
}

interface OwnProps {
	onChange: (jobOption: Nullable<JobFilterOption>) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const JobFilter: React.FC<Props> = (props) => {

	const {
		onChange,
		findAllForOrderReportFilter,
	} = props;

	const [options, setOptions] = React.useState<JobFilterOption[]>([]);

	const fetchOptions = React.useCallback(async () => {
		const _options = await findAllForOrderReportFilter();
		const formattedOptions = JobFilterOption.bulkConstructor(_options.workRequests);
		setOptions(formattedOptions);
	}, [findAllForOrderReportFilter]);

	React.useEffect(() => {
		fetchOptions();
	}, [fetchOptions]);

	const renderOption = React.useCallback((option: JobFilterOption) => {
		if (!option.jobCode) {
			return <div />;
		}
		return (
			<div>
				<strong>{option.jobCode} | {option.title}</strong>
				<small><br /></small>
				<small>{option.customerCompany}</small>
			</div>
		);
	}, []);

	const renderSelected = React.useCallback((option: JobFilterOption) => {
		return (
			<>
				{option.jobCode}
			</>
		);
	}, []);

	const filterBy = React.useCallback((option: JobFilterOption, rawInput: string) => {
		return option.title.toLowerCase().includes(rawInput.toLowerCase())
			|| option.jobCode.toLowerCase().includes(rawInput.toLowerCase())
			|| !!option.customerCompany?.toLowerCase().includes(rawInput.toLowerCase());
	}, []);

	return (
		<div className={styles.dropdown}>
			<Dropdown<JobFilterOption>
				filterable={true}
				filterBy={filterBy}
				hasBlankOption={true}
				onValueChange={onChange}
				options={options}
				placeholder="Select Job"
				renderMenuItem={renderOption}
				renderSelected={renderSelected}
				valueKey={'id'}
				withCaret={true}
			/>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		findAllForOrderReportFilter: WorkRequestActions.findAllForOrderReportFilter,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(JobFilter);
